/**
 * Ground Variation — fully procedural grass shader.
 *
 * Replaces the previous tile-based diffuse + roughness textures with
 * shader-generated noise so the ground never reads as repeating tiles.
 * The 48×48 grass-albedo.webp tile was creating visible horizontal
 * banding when foreshortened by the camera; this rewrite generates
 * the entire diffuse and roughness from world-space value noise so
 * there is no UV repeat to leak through.
 *
 * Three octaves of value-noise are layered on world XZ:
 *   - macro  (~6 m)   — warm/cool hue drift across regions
 *   - medium (~1.5 m) — drier vs lush patches (brightness clumps)
 *   - fine   (~0.4 m) — high-frequency speckle (per-blade-cluster grain)
 *
 * Plus a soft edge fade toward the horizon color and a roughness
 * variation tied to the medium octave (drier patches → rougher).
 *
 * Symmetric with woodVariation.ts: same fail-fast `replaceShaderChunkOrThrow`
 * guard so a Three.js upgrade that renames a shader chunk surfaces loudly
 * instead of silently turning the patch into a no-op.
 */
import * as THREE from "three";

interface GroundVariationParams {
	/** World radius (m) where edge fade begins. Default: 11.0 */
	edgeFadeStart?: number;
	/** World radius (m) where edge fade reaches full strength. Default: 16.0 */
	edgeFadeEnd?: number;
	/** Color the ground fades toward at the rim. Default: 0x6f7767 */
	edgeFadeColor?: number;
	/** Maximum amount the edge fade darkens/shifts the diffuse color. Default: 0.85 */
	edgeFadeStrength?: number;
	/** Period (m) of macro variation. Default: 6.0 */
	macroPeriodMeters?: number;
	/** Cool / shaded grass tone. Default: 0x4d6b40 */
	grassCoolColor?: number;
	/** Warm / sunlit grass tone. Default: 0x738a52 */
	grassWarmColor?: number;
	/** Dry / parched grass patches. Default: 0x8c8a57 */
	grassDryColor?: number;
	/** Strength of the per-cluster fine speckle (0..1). Default: 0.18 */
	fineSpeckleStrength?: number;
	/** Strength of the medium "drier patches" lerp toward `grassDryColor` (0..1). Default: 0.55 */
	dryPatchStrength?: number;
	/** Roughness modulation amplitude. Default: 0.18 */
	macroRoughnessRange?: number;
}

const DEFAULT_PARAMS: Required<GroundVariationParams> = {
	edgeFadeStart: 11.0,
	edgeFadeEnd: 16.0,
	edgeFadeColor: 0x6f7767,
	edgeFadeStrength: 0.85,
	macroPeriodMeters: 6.0,
	grassCoolColor: 0x465543,
	grassWarmColor: 0x70745b,
	grassDryColor: 0x938a70,
	fineSpeckleStrength: 0.12,
	dryPatchStrength: 0.32,
	macroRoughnessRange: 0.18,
};

type ShaderStage = "vertex" | "fragment";

function replaceShaderChunkOrThrow(
	shaderSource: string,
	chunk: string,
	replacement: string,
	stage: ShaderStage,
): string {
	if (!shaderSource.includes(chunk)) {
		throw new Error(
			`[groundVariation] Missing ${stage} shader chunk "${chunk}". ` +
				"Three.js shader layout changed; update applyGroundVariation injection points.",
		);
	}

	return shaderSource.replace(chunk, replacement);
}

const VARIATION_GLSL = /* glsl */ `
  float groundHash(vec2 co) {
    return fract(sin(dot(co, vec2(91.345, 47.853))) * 23456.789);
  }

  // Smoothed value noise, period 1 in input units. Caller scales coords.
  float groundValueNoise(vec2 p) {
    vec2 i = floor(p);
    vec2 f = fract(p);
    vec2 u = f * f * (3.0 - 2.0 * f);
    float a = groundHash(i);
    float b = groundHash(i + vec2(1.0, 0.0));
    float c = groundHash(i + vec2(0.0, 1.0));
    float d = groundHash(i + vec2(1.0, 1.0));
    return mix(mix(a, b, u.x), mix(c, d, u.x), u.y);
  }
`;

/**
 * Apply procedural grass shading to a MeshStandardMaterial.
 *
 * The material does not need a diffuse `map` or `roughnessMap` — both
 * are computed in the fragment shader. A `normalMap` is still
 * respected if attached (some installs may want a low-frequency dirt
 * normal for raking sun) but is not required.
 *
 * Mutates the material's `onBeforeCompile` — safe to call once
 * during material creation. Forces shader recompilation.
 */
export function applyGroundVariation(
	material: THREE.MeshStandardMaterial,
	params: GroundVariationParams = {},
): void {
	const p = { ...DEFAULT_PARAMS, ...params };
	const macroFreq = 1.0 / p.macroPeriodMeters;
	const fadeColor = new THREE.Color(p.edgeFadeColor);
	const grassCool = new THREE.Color(p.grassCoolColor);
	const grassWarm = new THREE.Color(p.grassWarmColor);
	const grassDry = new THREE.Color(p.grassDryColor);

	material.onBeforeCompile = (shader) => {
		// ── Vertex: declare varying + capture world position ──
		shader.vertexShader = replaceShaderChunkOrThrow(
			shader.vertexShader,
			"#include <common>",
			`#include <common>
varying vec3 vGroundWorldPos;`,
			"vertex",
		);
		shader.vertexShader = replaceShaderChunkOrThrow(
			shader.vertexShader,
			"#include <worldpos_vertex>",
			`#include <worldpos_vertex>
vGroundWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;`,
			"vertex",
		);

		// ── Fragment: declarations + helpers ──
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <common>",
			`#include <common>
varying vec3 vGroundWorldPos;
${VARIATION_GLSL}`,
			"fragment",
		);

		// ── Fragment: generate diffuse from scratch + edge fade ──
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <color_fragment>",
			`#include <color_fragment>
{
  vec2 wp = vGroundWorldPos.xz;

  // Three octaves of value noise. The frequency ratios are
  // intentionally non-integer so the octaves never line up into a
  // visible repeat.
  float nMacro  = groundValueNoise(wp * ${macroFreq.toFixed(5)});
  float nMedium = groundValueNoise(wp * ${(macroFreq * 4.13).toFixed(5)} + vec2(31.4, 17.1));
  float nFine   = groundValueNoise(wp * ${(macroFreq * 16.71).toFixed(5)} + vec2(73.1, 41.9));

  // Macro hue: cool/warm bias across ~6 m regions.
  vec3 cool = vec3(${grassCool.r.toFixed(4)}, ${grassCool.g.toFixed(4)}, ${grassCool.b.toFixed(4)});
  vec3 warm = vec3(${grassWarm.r.toFixed(4)}, ${grassWarm.g.toFixed(4)}, ${grassWarm.b.toFixed(4)});
  vec3 dry  = vec3(${grassDry.r.toFixed(4)}, ${grassDry.g.toFixed(4)}, ${grassDry.b.toFixed(4)});
  vec3 grass = mix(cool, warm, nMacro);

  // Medium scale: drier patches lerp toward the dry tone where the
  // medium-octave value rises. smoothstep keeps the transition soft.
  float dryMix = smoothstep(0.55, 0.85, nMedium) * ${p.dryPatchStrength.toFixed(4)};
  grass = mix(grass, dry, dryMix);

  // Fine speckle: per-cluster lightness jitter. Multiplied so the
  // hue stays stable — only luminance varies at this scale.
  grass *= 1.0 + (nFine - 0.5) * ${p.fineSpeckleStrength.toFixed(4)};

  // Replace, not multiply — there is no diffuse texture under us.
  diffuseColor.rgb = grass;

  // Edge fade: blend toward horizon color radially. Soft so it reads
  // as atmospheric perspective, not a hard cutoff.
  float radius = length(wp);
  float fadeT = smoothstep(${p.edgeFadeStart.toFixed(2)}, ${p.edgeFadeEnd.toFixed(2)}, radius);
  vec3 edgeColor = vec3(${fadeColor.r.toFixed(4)}, ${fadeColor.g.toFixed(4)}, ${fadeColor.b.toFixed(4)});
  diffuseColor.rgb = mix(diffuseColor.rgb, edgeColor, fadeT * ${p.edgeFadeStrength.toFixed(4)});

  diffuseColor.rgb = clamp(diffuseColor.rgb, 0.0, 1.0);
}
`,
			"fragment",
		);

		// ── Fragment: roughness generated from medium octave ──
		// Drier patches (high nMedium) read as rougher / less specular —
		// helps separate them from the lush areas under low sun.
		shader.fragmentShader = replaceShaderChunkOrThrow(
			shader.fragmentShader,
			"#include <roughnessmap_fragment>",
			`#include <roughnessmap_fragment>
{
  vec2 wp = vGroundWorldPos.xz;
  float n = groundValueNoise(wp * ${(macroFreq * 4.13).toFixed(5)} + vec2(31.4, 17.1));
  roughnessFactor = clamp(roughnessFactor + (n - 0.5) * ${p.macroRoughnessRange.toFixed(4)}, 0.0, 1.0);
}
`,
			"fragment",
		);
	};

	material.needsUpdate = true;
}
